import { FC, useEffect, useState } from 'react';
import styles from './MainPanel.module.scss';
import FlyingHoursTab from '../../tabs/FlyingHoursTab';
import { GoAButton, GoAButtonGroup, GoAModal } from '@abgov/react-components';
import { AuthenticationService } from '../../../../services/authentication/AuthenticationService';
import { validate } from '../../../../utils/commonMethods';
import TabsInside from './Tabs';
import { useAuth } from 'react-oidc-context';
import { IFlightReport } from '../../../../model/IFlightReport';
import { IPaginationResult, PaginationResult } from '../../../../model/search/IPaginationResult';
import { IContractRates } from '../../../../model/HiringContract/IContractRates';
import { HiringContractService } from '../../../../services/HiringContractService';
import { IHiringContract } from '../../../../model/HiringContract/IHiringContract';
import { IFlyingHours } from '../../../../model/IFlyingHours';
import { convertCostCentersToArrayOptions } from '../../../../services/1GXCoding/CostCenterService';
import { convertInternalOrdersToArrayOptions } from '../../../../services/1GXCoding/InternalOrderService';
import { convertCostingActivitiesToArrayOptions } from '../../../../services/domainServices/CostingActivityService';
import { convertCrewTypesToArrayOptions } from '../../../../services/domainServices/CrewTypeService';
import { convertFireNumbersToArrayOptions } from '../../../../services/domainServices/FireNumberService';
import { IDomainData } from '../../helpers/DomainDataHelper';
import { convertAccountsToArrayOptions } from '../../../../services/1GXCoding/AccountService';
import { convertFundsToArrayOptions } from '../../../../services/1GXCoding/FundService';

import { IArrayOption } from '../../../../model/IArrayOption';
import CalloutVariant from '../../../../model/enum/CalloutVariant';
import EditExpenseDetails from '../../../expense/EditExpenseDetails';
import ViewExpenseDetails from '../../../expense/ViewExpenseDetails';
import { IFlightExpenses } from '../../../../model/IFlightExpense';
import FlightReportNotes from '../../../notes/FlightReportNotes';
import { FlightReportNote } from '../../../../model/IFlightReportNote';
import EditStandToStandDownTime, { setUsStandToStandDownErrors } from '../../../Unserviceable/EditStandToStandDownTime';
import ViewStandToStandDownTime from '../../../Unserviceable/ViewStandToStandDownTime';
import ViewUSLog from '../../../Unserviceable/ViewUSLog';
import EditUSLog, { setUsLogsErrors } from '../../../Unserviceable/EditUSLog';
import { IUSLog } from '../../../../model/Unserviceable/IUSLog';
import { USStandToStandDown } from '../../../../model/Unserviceable/IUSStandToStandDown';
import FlightReportStatus from '../../../../model/enum/FlightReportStatus';

const { container } = styles;

interface IMainPanelProps {
  flightReport: IFlightReport;
  ruleCode: string;
  isChildDataValid: boolean;
  expensesChanged: boolean;
  flyingHoursChanged: boolean;
  usStandToStandDownChanged: boolean;
  usLogsChanged: boolean;
  onError: (errorMessages: string[]) => void;
  setWarning(warning: any): void;
  setError(error: any): void;
  setIsLoading(isLoading: boolean): void;
  domainData: IDomainData;
  onSaveFlyingHours: () => Promise<boolean>;
  onCancelFlyingHours: () => void;
  onChangeFlyingHours: (flyingHours: IFlyingHours[]) => void;
  flyingHours: IFlyingHours[];
  expenses: IFlightExpenses;
  onSaveExpenses: () => Promise<boolean>;
  onCancelExpenses: () => void;
  onChangeExpenses: (expenses: IFlightExpenses) => void;
  notes: FlightReportNote[];
  onNotesChange: () => void;
  uslogs: IUSLog[];
  onSaveUSLogs: () => Promise<boolean>;
  onCancelUSLogs: () => void;
  onChangeUSLogs: (uSLogs: IUSLog[]) => void;
  uSStandToStandDown?: USStandToStandDown;
  onUsStandToStandDownSave: () => Promise<boolean>;
  onUsStandToStandDownCancel: () => void;
  onUsStandToStandDownChange: (uSStandToStandDown: USStandToStandDown) => void;
  showUnserviceableReportTab: boolean;
}
const MainPanel: FC<IMainPanelProps> = ({
  flightReport,
  ruleCode,
  isChildDataValid,
  expensesChanged,
  flyingHoursChanged,
  usStandToStandDownChanged,
  usLogsChanged,
  onError,
  setWarning,
  setError,
  setIsLoading,
  domainData,
  onSaveFlyingHours,
  onCancelFlyingHours,
  onChangeFlyingHours,
  flyingHours,
  expenses,
  onSaveExpenses,
  onCancelExpenses,
  onChangeExpenses,
  notes,
  onNotesChange,
  uslogs,
  onSaveUSLogs,
  onCancelUSLogs,
  onChangeUSLogs,
  uSStandToStandDown,
  onUsStandToStandDownSave,
  onUsStandToStandDownCancel,
  onUsStandToStandDownChange,
  showUnserviceableReportTab,
}) => {
  const auth = useAuth();
  const [contractRatesPaginationResult, setContractRatesPaginationResult] = useState<IPaginationResult<IContractRates>>(
    new PaginationResult<IContractRates>(),
  );

  const [fireNumberOptions, setFireNumberOptions] = useState<IArrayOption[]>([]);
  const [costingActivityOptions, setCostingActivityOptions] = useState<IArrayOption[]>([]);
  const [crewTypeOptions, setCrewTypeOptions] = useState<IArrayOption[]>([]);

  //1GX coding financial
  const [internalOrderOptions, setInternalOrderOptions] = useState<IArrayOption[]>([]);
  const [costCenterOptions, setCostCenterOptions] = useState<IArrayOption[]>([]);
  const [fundOptions, setFundOptions] = useState<IArrayOption[]>([]);
  const [accountOptions, setAccountOptions] = useState<IArrayOption[]>([]);

  const [notificationMessage, setNotificationMessage] = useState('');

  const { contractRegistration, flightReportDate } = flightReport ?? {};

  useEffect(() => {
    if (!flightReportDate || !contractRegistration) return;

    const run = async () => {
      const token = auth.user?.access_token as string;
      if (!token) return;

      try {
        const response = await HiringContractService.getRates(
          flightReport.contractRegistration as IHiringContract,
          flightReport.flightReportDate,
          token,
        );

        if (response.data) {
          setContractRatesPaginationResult(response.data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    run();
  }, [flightReport, flightReportDate, contractRegistration, auth.user?.access_token]);

  const [editExpenses, setEditExpenses] = useState<boolean>(false);
  const [editUnserviceableStandToStandDown, setEditUnserviceableStandToStandDown] = useState<boolean>(false);

  const [editUSLog, setEditUSLog] = useState<boolean>(false);

  useEffect(() => {
    setFireNumberOptions(convertFireNumbersToArrayOptions(domainData.fireNumbers));
    setCrewTypeOptions(convertCrewTypesToArrayOptions(domainData.crewTypes));
    const costingActivityOptions = convertCostingActivitiesToArrayOptions(domainData.costingActivities);
    setCostingActivityOptions(costingActivityOptions);

    setInternalOrderOptions(convertInternalOrdersToArrayOptions(domainData.internalOrders));
    setCostCenterOptions(convertCostCentersToArrayOptions(domainData.costCenters));
    setFundOptions(convertFundsToArrayOptions(domainData.funds));
    setAccountOptions(convertAccountsToArrayOptions(domainData.accounts));
  }, [domainData]);

  return (
    <div className={container}>
      <TabsInside
        initialTab={0}
        tabs={[
          {
            buttonLabel: 'Flying hours',
            component: (
              <>
                <FlyingHoursTab
                  flightReport={flightReport}
                  flyingHours={flyingHours}
                  noFlyDay={flightReport.noFlyDay}
                  isValid={isChildDataValid && flyingHoursChanged}
                  canEdit={
                    flightReport.status !== FlightReportStatus.processed &&
                    auth.isAuthenticated &&
                    flightReport.flightReportId > 0 &&
                    AuthenticationService.validateRolesAndPermission(auth, flightReport)
                  }
                  canSave={
                    auth.isAuthenticated &&
                    AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRpt_W', auth.user?.access_token as string)
                  }
                  onValidate={(element, event, value) => {
                    const errors = validate(ruleCode, element, event, value);
                    if (errors.length > 0) {
                      onError(errors);
                    }
                  }}
                  contractRates={contractRatesPaginationResult}
                  onSave={onSaveFlyingHours}
                  onCancel={onCancelFlyingHours}
                  onChange={onChangeFlyingHours}
                  domainData={domainData}
                />
              </>
            ),
          },

          {
            buttonLabel: 'Expenses',
            component: (
              <>
                {!editExpenses && expenses && flightReport.flightReportId && (
                  <div className='right paddingRight20px'>
                    {flightReport.status !== FlightReportStatus.processed &&
                      auth.isAuthenticated &&
                      AuthenticationService.validateRolesAndPermission(auth, flightReport) && (
                        <div>
                          <GoAButton
                            size='compact'
                            type='tertiary'
                            onClick={() => {
                              setEditExpenses(true);
                            }}
                          >
                            Edit
                          </GoAButton>
                        </div>
                      )}
                  </div>
                )}

                {editExpenses ? (
                  <div className='right paddingRight20px'>
                    {auth.isAuthenticated &&
                      AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRpt_W', auth.user?.access_token as string) && (
                        <div>
                          <GoAButton
                            size='compact'
                            type='tertiary'
                            disabled={!isChildDataValid || !expensesChanged}
                            onClick={async () => {
                              const success = await onSaveExpenses();
                              setEditExpenses(!success);
                            }}
                          >
                            Save
                          </GoAButton>
                          <GoAButton
                            size='compact'
                            type='tertiary'
                            onClick={() => {
                              onCancelExpenses();
                              setEditExpenses(false);
                            }}
                          >
                            Cancel
                          </GoAButton>
                        </div>
                      )}
                  </div>
                ) : (
                  <></>
                )}
                <div className='expense-section white-background'>
                  {editExpenses ? (
                    <div>
                      <EditExpenseDetails
                        flightExpenses={expenses}
                        contractRatesPaginationResult={contractRatesPaginationResult}
                        flightReport={flightReport}
                        fireNumberOptions={fireNumberOptions}
                        crewTypeOptions={crewTypeOptions}
                        costingActivityOptions={costingActivityOptions}
                        internalOrderOptions={internalOrderOptions}
                        costCenterOptions={costCenterOptions}
                        fundOptions={fundOptions}
                        accountOptions={accountOptions}
                        onChange={onChangeExpenses}
                      />
                    </div>
                  ) : (
                    <div>
                      <ViewExpenseDetails expensesData={expenses} />
                    </div>
                  )}
                </div>
              </>
            ),
          },

          {
            buttonLabel: 'Notes ' + (notes?.length ?? 0),
            component: (
              <>
                <FlightReportNotes
                  onChange={onNotesChange}
                  flightReportNoteList={notes}
                  flightReportId={flightReport?.flightReportId}
                ></FlightReportNotes>
              </>
            ),
          },

          // //#region Unserviceable Reports
          {
            hidden: !showUnserviceableReportTab,
            buttonLabel: 'Unserviceable Reports',
            component: (
              <>
                {!editUnserviceableStandToStandDown ? (
                  <div className='right paddingRight20px'>
                    {flightReport.status !== FlightReportStatus.processed &&
                      auth.isAuthenticated &&
                      AuthenticationService.validateRolesAndPermission(auth, flightReport) && (
                        <div>
                          <GoAButton
                            size='compact'
                            type='tertiary'
                            onClick={() => {
                              setEditUnserviceableStandToStandDown(true);
                            }}
                          >
                            Edit
                          </GoAButton>
                        </div>
                      )}
                  </div>
                ) : (
                  <></>
                )}

                {editUnserviceableStandToStandDown ? (
                  <div className='right paddingRight20px'>
                    {auth.isAuthenticated &&
                      AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRpt_W', auth.user?.access_token as string) && (
                        <div>
                          <GoAButton
                            size='compact'
                            type='tertiary'
                            disabled={!usStandToStandDownChanged || !isChildDataValid || setUsStandToStandDownErrors(uSStandToStandDown)}
                            onClick={async () => {
                              const success = await onUsStandToStandDownSave();
                              setEditUnserviceableStandToStandDown(!success);
                            }}
                          >
                            Save
                          </GoAButton>
                          <GoAButton
                            size='compact'
                            type='tertiary'
                            onClick={() => {
                              onUsStandToStandDownCancel();
                              setEditUnserviceableStandToStandDown(false);
                            }}
                          >
                            Cancel
                          </GoAButton>
                        </div>
                      )}
                  </div>
                ) : (
                  <></>
                )}
                <div className='unserviceableReport-section white-background'>
                  {editUnserviceableStandToStandDown ? (
                    <EditStandToStandDownTime
                      flightReport={flightReport}
                      uSStandToStandDown={uSStandToStandDown}
                      onChange={onUsStandToStandDownChange}
                    />
                  ) : (
                    <div>
                      <ViewStandToStandDownTime uSStandToStandDown={uSStandToStandDown} />
                    </div>
                  )}
                </div>
                {/* #endregion "Unserviceable Start Time/End Time section" */}

                {/* #region "Unserviceable Log section" */}
                {!editUSLog ? (
                  <div className='right unserviceableLog'>
                    {flightReport.status !== FlightReportStatus.processed &&
                      auth.isAuthenticated &&
                      AuthenticationService.validateRolesAndPermission(auth, flightReport) && (
                        <div>
                          <GoAButton
                            size='compact'
                            type='tertiary'
                            onClick={() => {
                              setEditUSLog(true);
                            }}
                          >
                            Edit
                          </GoAButton>
                        </div>
                      )}
                  </div>
                ) : (
                  <></>
                )}

                {editUSLog ? (
                  <div className='right unserviceableLog'>
                    {auth.isAuthenticated &&
                      AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRpt_W', auth.user?.access_token as string) && (
                        <div>
                          <GoAButton
                            size='compact'
                            type='tertiary'
                            disabled={!usLogsChanged || !isChildDataValid || setUsLogsErrors(uslogs)}
                            onClick={async () => {
                              const success = await onSaveUSLogs();
                              setEditUSLog(!success);
                            }}
                          >
                            Save
                          </GoAButton>
                          <GoAButton
                            size='compact'
                            type='tertiary'
                            onClick={async () => {
                              setEditUSLog(false);
                              onCancelUSLogs();
                            }}
                          >
                            Cancel
                          </GoAButton>
                        </div>
                      )}
                  </div>
                ) : (
                  <></>
                )}
                <div className='unserviceableReport-section white-background'>
                  {editUSLog ? (
                    <EditUSLog flightReport={flightReport} arrUSLogs={uslogs} onChange={onChangeUSLogs} />
                  ) : (
                    <div className='paddingTop20px'>
                      <ViewUSLog logs={uslogs} />
                    </div>
                  )}
                </div>
              </>
            ),
          },
          //#endregion Unserviceable Reports
        ]}
      ></TabsInside>

      <GoAModal
        heading={''}
        calloutVariant={CalloutVariant.success}
        open={notificationMessage !== ''}
        actions={
          <GoAButtonGroup alignment='end'>
            <GoAButton
              onClick={() => {
                setNotificationMessage('');
              }}
            >
              Okay
            </GoAButton>
          </GoAButtonGroup>
        }
      >
        <div dangerouslySetInnerHTML={{ __html: notificationMessage }} />
      </GoAModal>
    </div>
  );
};
export default MainPanel;
